import React, { useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';

const breakpoints = {
  mobile: '320px',
  tablet: '768px',
  laptop: '1024px',
};

const Container = styled.div`
  margin-top: 32px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  width: 100%;
  padding-left: 32px;
  border-top: 3px solid #E6E6E6;
  padding-top: 24px;
  padding-bottom: 24px;

  @media (max-width: ${breakpoints.mobile}) {
    padding-left: 16px;
    padding-right: 16px;
  }
`;

const Title = styled.h3`
  font-size: 16px;
  color: #4a5568;
  margin-bottom: 16px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  padding: 0 50px;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 0;
    text-align: left;
  }
`;

const TimeStamp = styled.div`
  color: #718096;
  font-size: 16px;
  margin-bottom: 24px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  padding: 0 50px;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 0;
    font-size: 14px;
  }
`;

const ScoreContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  width: 100%;
  max-width: 1200px;
  padding: 0;
  justify-content: flex-start;
  margin: 0 0 32px 0;
  
  @media (max-width: ${breakpoints.tablet}) {
    flex-wrap: wrap;
    justify-content: center;
    gap: 16px;
  }
`;

const ScoreCard = styled.div`
  flex: 0 0 176px;
  height: 142px;
  background: ${props => getScoreBackgroundColor(props.percentage)};
  border: 1px solid ${props => getScoreBorderColor(props.percentage)};
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  text-align: center;
  gap: 12px;
  scroll-snap-align: start;

  @media (max-width: ${breakpoints.mobile}) {
    flex: 0 0 100%;
    height: auto;
    min-height: 120px;
  }
`;

const ScoreTitle = styled.div`
  font-size: 16px;
  color: #4a5568;
  font-weight: 400;
  font-family: 'Plus Jakarta Sans', sans-serif;
  text-align: center;
  line-height: 1.4;
`;

const ScoreValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #2d3748;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: #4299e1;
  font-size: 14px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  cursor: pointer;
  padding: 8px 0;
  margin-top: 16px;
  text-decoration: underline;
`;

const SectionTitle = styled.h4`
  font-size: 16px;
  color: #4a5568;
  margin: 32px 0 16px 0;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  padding: 0 50px;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 0;
    margin: 24px 0 12px 0;
  }
`;

const CategoryContainer = styled.div`
  margin-bottom: 24px;
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: ${props => getCategoryBackgroundColor(props.percentage)};
  border: 1px solid ${props => getCategoryBorderColor(props.percentage)};
  border-radius: 8px;
  margin-bottom: 16px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

const CategoryHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CategoryIcon = styled.span`
  font-size: 18px;
  transition: transform 0.2s;
  transform: ${props => props.isOpen ? 'rotate(90deg)' : 'rotate(0deg)'};
  color: #718096;
`;

const CategoryName = styled.div`
  font-size: 16px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 600;
  color: #4a5568;
`;

const CategoryScore = styled.div`
  font-size: 16px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 600;
  color: #2d3748;
`;

// Group container for organizing similar topics
const TopicGroupContainer = styled.div`
  margin-bottom: 20px;
`;

const TopicsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-top: 16px;
  padding: 0 20px;
  width: 100%;
  
  @media (max-width: ${breakpoints.laptop}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 18px;
  }
  
  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    padding: 0 15px;
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 0 10px;
  }
`;

const TopicCard = styled.div`
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  min-height: 140px;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: ${breakpoints.tablet}) {
    padding: 14px;
    min-height: 130px;
  }
  
  @media (max-width: 600px) {
    padding: 12px;
    min-height: 120px;
  }
`;

const TopicName = styled.div`
  font-size: 16px;
  color: #4a5568;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 600;
  margin-bottom: 12px;
  line-height: 1.3;
  
  @media (max-width: ${breakpoints.tablet}) {
    font-size: 15px;
    margin-bottom: 10px;
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 14px;
    margin-bottom: 8px;
  }
`;

const TopicStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  font-size: 14px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  flex: 1;
  
  @media (max-width: ${breakpoints.tablet}) {
    gap: 6px;
    font-size: 13px;
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    gap: 4px;
    font-size: 12px;
  }
`;

const TopicStat = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 2px 0;
  }
`;

const StatLabel = styled.span`
  color: #718096;
`;

const StatValue = styled.span`
  font-weight: 600;
  color: ${props => {
    if (props.type === 'correct') return '#38a169';
    if (props.type === 'incorrect') return '#e53e3e';
    if (props.type === 'unattempted') return '#a0aec0';
    return '#4a5568';
  }};
`;

// Helper functions for color coding - Updated with lighter colors
const getScoreBackgroundColor = (percentage) => {
  if (percentage >= 70) return '#e6fffa';
  if (percentage >= 50) return '#fffbeb';
  if (percentage >= 30) return '#fef5e7';
  return '#fed7d7';
};

const getScoreBorderColor = (percentage) => {
  if (percentage >= 70) return '#4fd1c7';
  if (percentage >= 50) return '#f6ad55';
  if (percentage >= 30) return '#ed8936';
  return '#fc8181';
};

const getCategoryBackgroundColor = (percentage) => {
  if (percentage >= 70) return '#f0fff4';
  if (percentage >= 50) return '#fffaf0';
  if (percentage >= 30) return '#fff5f5';
  return '#fef5e7';
};

const getCategoryBorderColor = (percentage) => {
  if (percentage >= 70) return '#9ae6b4';
  if (percentage >= 50) return '#fbd38d';
  if (percentage >= 30) return '#feb2b2';
  return '#fbb6ce';
};

// Helper function to group topics by their main category and aggregate stats
const groupTopicsByMainCategory = (topics) => {
  const groups = {};
  
  topics.forEach(topic => {
    // Extract main category from topic name (part before colon)
    const parts = topic.name.split(':');
    const mainCategory = parts.length > 1 ? parts[0].trim() : 'General';
    
    if (!groups[mainCategory]) {
      groups[mainCategory] = {
        name: mainCategory,
        total: 0,
        gained: 0,
        right: 0,
        wrong: 0,
        unattempted: 0,
        topics: []
      };
    }
    
    // Aggregate the stats
    groups[mainCategory].total += topic.total;
    groups[mainCategory].gained += topic.gained;
    groups[mainCategory].right += topic.right;
    groups[mainCategory].wrong += topic.wrong;
    groups[mainCategory].unattempted += topic.unattempted;
    groups[mainCategory].topics.push(topic);
  });
  
  return groups;
};

const AptitudeAssessment = ({ data }) => {
  const [showCategories, setShowCategories] = useState(false);
  const [openCategories, setOpenCategories] = useState({});

  if (!data) return null;

  const formatDateTime = (timestamp) => ({
    date: moment(timestamp).format('DD/MM/YYYY'),
    time: moment(timestamp).format('hh:mm A'),
  });

  const { date, time } = formatDateTime(data.timestamp);
  
  // Overall percentage comes from the API (derived from the authoritative
  // statistics summary, the same source as the category breakdown below and the
  // institute report). Fall back to computing it only for older API responses
  // that predate the overallPercentage field.
  const overallPercentage = typeof data.overallPercentage === 'number'
    ? data.overallPercentage
    : Math.round((data.gainedScore / data.totalScore) * 100);
  
  // Calculate total questions from categories
  const totalQuestions = data.categories.reduce((sum, cat) => 
    sum + cat.topics.reduce((topicSum, topic) => 
      topicSum + (topic.right + topic.wrong + topic.unattempted), 0), 0);
  
  const totalCorrect = data.categories.reduce((sum, cat) => 
    sum + cat.topics.reduce((topicSum, topic) => topicSum + topic.right, 0), 0);
  
  const totalAttempted = data.categories.reduce((sum, cat) => 
    sum + cat.topics.reduce((topicSum, topic) => topicSum + (topic.right + topic.wrong), 0), 0);
  
  const totalUnattempted = totalQuestions - totalAttempted;

  const toggleCategory = (categoryIndex) => {
    setOpenCategories(prev => ({
      ...prev,
      [categoryIndex]: !prev[categoryIndex]
    }));
  };

  return (
    <Container>
      <Title>Aptitude Assessment</Title>
      <TimeStamp>
        Time: <strong>{date} | {time}</strong>
      </TimeStamp>
      
      <ScoreContainer>
        <ScoreCard percentage={overallPercentage}>
          <ScoreTitle>Overall Score</ScoreTitle>
          <ScoreValue>{data.gainedScore}/{data.totalScore}</ScoreValue>
        </ScoreCard>
        
        <ScoreCard percentage={overallPercentage}>
          <ScoreTitle>Percentage</ScoreTitle>
          <ScoreValue>{overallPercentage}%</ScoreValue>
        </ScoreCard>

        <ScoreCard percentage={overallPercentage}>
          <ScoreTitle>Total Questions</ScoreTitle>
          <ScoreValue>{totalQuestions}</ScoreValue>
        </ScoreCard>

        <ScoreCard percentage={overallPercentage}>
          <ScoreTitle>Total Attempted</ScoreTitle>
          <ScoreValue>{totalAttempted}</ScoreValue>
        </ScoreCard>

        <ScoreCard percentage={totalUnattempted === 0 ? 100 : 0}>
          <ScoreTitle>Unattempted</ScoreTitle>
          <ScoreValue>{totalUnattempted}</ScoreValue>
        </ScoreCard>
      </ScoreContainer>

      {!showCategories && (
        <ToggleButton onClick={() => setShowCategories(true)}>
          See category breakdown
        </ToggleButton>
      )}

      {showCategories && (
        <>
          <SectionTitle>Category-wise Breakdown</SectionTitle>
          {data.categories.map((category, index) => {
            const categoryPercentage = Math.round((category.gained_marks / category.total_marks) * 100);
            const isOpen = openCategories[index];
            
            // Group topics by their main category
            const groupedTopics = groupTopicsByMainCategory(category.topics);
            
            return (
              <CategoryContainer key={index}>
                <CategoryHeader 
                  percentage={categoryPercentage}
                  onClick={() => toggleCategory(index)}
                >
                  <CategoryHeaderLeft>
                    <CategoryIcon isOpen={isOpen}>▶</CategoryIcon>
                    <CategoryName>{category.name}</CategoryName>
                  </CategoryHeaderLeft>
                  <CategoryScore>{category.gained_marks}/{category.total_marks} ({categoryPercentage}%)</CategoryScore>
                </CategoryHeader>
                
                {isOpen && category.topics && category.topics.length > 0 && (
                  <TopicsGrid>
                    {Object.entries(groupedTopics).map(([groupName, groupData]) => (
                      <TopicCard key={groupName}>
                        <TopicName>{groupName}</TopicName>
                        <TopicStats>
                          <TopicStat>
                            <StatLabel>Correct:</StatLabel>
                            <StatValue type="correct">{groupData.right}</StatValue>
                          </TopicStat>
                          <TopicStat>
                            <StatLabel>Incorrect:</StatLabel>
                            <StatValue type="incorrect">{groupData.wrong}</StatValue>
                          </TopicStat>
                          <TopicStat>
                            <StatLabel>Unattempted:</StatLabel>
                            <StatValue type="unattempted">{groupData.unattempted}</StatValue>
                          </TopicStat>
                          <TopicStat>
                            <StatLabel>Score:</StatLabel>
                            <StatValue>
                              {groupData.gained}/{groupData.total} ({groupData.total > 0 ? Math.round((groupData.gained / groupData.total) * 100) : 0}%)
                            </StatValue>
                          </TopicStat>
                        </TopicStats>
                      </TopicCard>
                    ))}
                  </TopicsGrid>
                )}
              </CategoryContainer>
            );
          })}
          <ToggleButton onClick={() => setShowCategories(false)}>
            Hide category breakdown
          </ToggleButton>
        </>
      )}
    </Container>
  );
};

export default AptitudeAssessment;